
import Vue from 'vue';
import moment from 'moment-timezone';
import { functions } from '@/firebase';
import _trim from 'lodash/trim';
import { mapActions, mapGetters, mapState } from 'vuex';
import { CompanyCustomFieldDef, CompanyCustomFieldKey } from '@run-diffusion/shared';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import ActionsIsland from '@/components/ActionsIsland.vue';
import {
	DEFAULT_TEXT_MAX_LENGTH,
	getShowTextCounterAt,
	getTextMaxLengthRule,
} from '@/utils';
import { TeamUserMixin } from '@/mixins';
import { JUST_ADDED_LIST, noteJustAdded } from '@/utils/justAdded';
import { justAddedTeamRow } from '@/views/Team/teamTablesState';
import SetTeamAllocationSettings from '../TeamDashboard/SetTeamAllocationSettings/SetTeamAllocationSettings.vue';
import { updateTeamTokenAllocationSettings } from '@/views/Team/TeamDashboard/SetTeamAllocationSettings/utils';
import { asyncRetry } from '@run-diffusion/shared';

export default Vue.extend({
	name: 'AddTeamToCompany',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: { type: Boolean, default: false },
		duplicateTeam: { type: Object, default: null },
	},
	data () {
		return {
			// FORM
			formValid: true,
			DEFAULT_TEXT_MAX_LENGTH,
			SHORT_TEXT_LENGTH: 100,
			rules: {
				name: [
					getTextMaxLengthRule(100),
					v => !!_trim(v) || 'Required',
				],
				customField: [
					getTextMaxLengthRule(),
				],
			},

			name: null,
			customFields: {} as Record<CompanyCustomFieldKey, any>,

			monthlyRunnitTokensBudget: 0,
			isBoundToBudget: false,
			leaveTeamUnfundedOnCreation: false,

			saving: false,
		};
	},
	computed: {
		...mapState([
			'team',
		]),
		...mapGetters([
			'displayLabels',
		]),
		sumMonthlyRunnitTokensBudget(): number {
			return this.team?.company?.sumMonthlyRunnitTokensBudget || 0;
		},
		teamCustomFieldDefs(): CompanyCustomFieldDef[] {
			return this.team?.company?.teamCustomFieldDefs || [];
		},
	},
	watch: {
		value: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal) {
					this.resetForm();
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		getShowTextCounterAt,
		setOpen (val: boolean) {
			this.$emit('input', !!val);
		},
		onClose () {
			this.setOpen(false);
		},
		onTeamAllocationSettingsInput ({ monthlyRunnitTokensBudget, isBoundToBudget, leaveTeamUnfundedOnCreation }) {
			this.monthlyRunnitTokensBudget = monthlyRunnitTokensBudget || 0;
			this.isBoundToBudget = !!isBoundToBudget;
			this.leaveTeamUnfundedOnCreation = !!leaveTeamUnfundedOnCreation;
		},
		updateTeamCustomField(fieldKey: CompanyCustomFieldKey, value: any) {
			this.customFields = {
				...this.customFields,
				[fieldKey]: value,
			};
		},
		resetForm () {
			this.name = this.duplicateTeam?.name ? `Copy of ${this.duplicateTeam.name}` : null;
			this.customFields = this.duplicateTeam?.customFields || {};
			this.onTeamAllocationSettingsInput(
				this.duplicateTeam?.allotmentSettings ||
				this.team?.company?.allotmentSettings?.defaults ||
				{},
			);
			this.$refs.form?.resetValidation();
		},
		async onSave () {
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			const trimmedName: string = _trim(this.name);
			const customFields: Record<CompanyCustomFieldKey, any> = {};
			this.teamCustomFieldDefs.forEach((fieldDef: CompanyCustomFieldDef) => {
				switch (fieldDef.type) {
					case 'STR': {
						const val: string = _trim(this.customFields[fieldDef.key]);
						if (val) customFields[fieldDef.key] = val;
						break;
					}
					default:
						break;
				}
			});

			const onError: Function = (e) => {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error saving ${this.displayLabels.team}, please reach out to report issues by going to Resources -> Help from the menu options`,
					show: true,
				});
			};
			try {
				this.saving = true;
				const functionRef = functions
					.httpsCallable('setupNewTeam');
				const { success, teamId } = (await functionRef({
					teamName: trimmedName,
					customFields,
					mode: 'ADD_TO_COMPANY',
					currTeamId: this.team.id,
					currCompanyId: this.team.companyId || null,
				})).data;
				if (!success) {
					onError(new Error('setupNewTeam returned success: false'));
				}
				// The company's Teams list has it only once the mirror does: pinned on top until then
				if (success && teamId && this.team.companyId) {
					noteJustAdded(JUST_ADDED_LIST.companyTeams(this.team.companyId), [{
						id: teamId,
						row: justAddedTeamRow({
							id: teamId,
							name: trimmedName,
							customFields: Object.keys(customFields).reduce((values: Record<string, string>, key: string) => ({
								...values,
								[key]: String(customFields[key]),
							}), {}),
							customFieldDefs: this.teamCustomFieldDefs || [],
							createdIso: moment().toISOString(),
						}),
					}]);
				}

				// Save allocation settings and transfer initial team tokens if has permission
				if (this.hasManageCompanyPermission) {
					try {
						await asyncRetry(
							() => updateTeamTokenAllocationSettings(
								functions.httpsCallable('updateTeamTokenAllocationSettings'),
								[{
									teamId,
									monthlyRunnitTokensBudget: this.monthlyRunnitTokensBudget,
									isBoundToBudget: this.isBoundToBudget
								}],
							),
						);
						if (!this.leaveTeamUnfundedOnCreation && this.monthlyRunnitTokensBudget > 0) {
							await asyncRetry(
								() => functions.httpsCallable('transferTeamTokens')({
									sourceTeamId: teamId,
									tokensFromCompany: this.monthlyRunnitTokensBudget,
								}),
							);
						}
					} catch (e) {
						console.error('Error saving allocation settings:', e);
						// Don't block the flow if allocation settings fail
					}
				}

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! ${this.displayLabels.team} saved: ${trimmedName}`,
					show: true,
				});
				this.$emit('success');
				this.onClose();
			} catch (e) {
				onError(e);
			} finally {
				this.saving = false;
				this.onClose();
				this.name = null;
				this.customFields = {};
			}
		},
	},
	components: {
		DialogOrBottomSheet,
		DialogContent,
		ActionsIsland,
		SetTeamAllocationSettings,
	},
});
